import { getActiveBrandId } from "@/config/brands";
import type { BrandId } from "@/config/brands/types";
import { brandMetaTemplates } from "./templates";
import type { MetaTemplate, MetaTemplateKey } from "./types";

export type { MetaTemplate, MetaTemplateKey, BrandMetaTemplates, BrandMetaTemplateRegistry } from "./types";
export { brandMetaTemplates } from "./templates";

/**
 * Map a concrete route (as passed to buildMetadata, e.g. "/", "/destinations",
 * "/installation-guide/", "/product/jp-5gb/") to its logical MetaTemplateKey.
 * Returns undefined for routes a brand template does not cover (legal pages,
 * blog, comparisons index, authors, calculator, etc.) — those keep their shared
 * meta.json copy.
 *
 * Route normalization mirrors getMeta(): strip leading/trailing slashes.
 */
export function resolveMetaTemplateKey(route: string): MetaTemplateKey | undefined {
  const normalized = route.replace(/^\/|\/$/g, "");
  const key = normalized === "" ? "home" : normalized;

  if (key === "home") return "home";
  if (key === "destinations") return "destinations";
  // Destination detail: /destinations/{code}/
  if (key.startsWith("destinations/")) return "destinationDetail";
  // Product detail: /product/{slug}/
  if (key.startsWith("product/") || key === "product") return "product";
  // How-it-works family.
  if (key === "installation-guide" || key === "how-to-choose-esim") return "howItWorks";
  if (key === "compatible-phones") return "compatibility";
  // Support family.
  if (key === "contact-us" || key === "faq") return "support";

  return undefined;
}

/**
 * Resolve the active brand's metadata template for a route, if one exists.
 *
 * Returns undefined when:
 *   - the active brand has no template registry (e.g. the default esimsupra), or
 *   - the route does not map to a templated page class, or
 *   - the brand registry omits that specific key.
 *
 * buildMetadata() falls back to the shared meta.json copy in all those cases,
 * preserving byte-identical default-brand output.
 */
export function getBrandMetaTemplate(
  route: string,
  brandId: BrandId = getActiveBrandId(),
): MetaTemplate | undefined {
  const templates = brandMetaTemplates[brandId];
  if (!templates) return undefined;
  const key = resolveMetaTemplateKey(route);
  if (!key) return undefined;
  return templates[key];
}
